import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Star, Clock, Users, Video, Calendar, ArrowLeft, DollarSign, Flag, ShieldCheck } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import StripeCheckout from '../components/StripeCheckout';
import Footer from '../components/Footer';
import SafetyReportDialog from '../components/SafetyReportDialog';

export default function Mentorship({ user }) {
  const [mentors, setMentors] = useState([]);
  const [mySessions, setMySessions] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [sessionDate, setSessionDate] = useState('');
  const [sessionTime, setSessionTime] = useState('');
  const [duration, setDuration] = useState(1);
  const [sessionType, setSessionType] = useState('1-on-1');
  const [notes, setNotes] = useState('');
  const [reportedMentor, setReportedMentor] = useState(null);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || localStorage.getItem('role') === 'admin';

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        const res = await api.get('/mentors');
        setMentors(res.data);
      } catch (error) {
        toast.error('Failed to load mentors');
      }
    };
    
    const fetchMySessions = async () => {
      try {
        const res = await api.get('/mentors/sessions/candidate');
        setMySessions(res.data);
      } catch (error) {
        console.error('Failed to load sessions');
      }
    };
    
    fetchMentors();
    if (user) {
      fetchMySessions();
    }
  }, [user]);

  const loadMySessions = async () => {
    try {
      const res = await api.get('/mentors/sessions/candidate');
      setMySessions(res.data);
    } catch (error) {
      console.error('Failed to load sessions');
    }
  };

  const handleBookSession = async () => {
    if (!sessionDate || !sessionTime) {
      toast.error('Please select date and time');
      return;
    }

    try {
      const res = await api.post('/mentors/sessions/book', {
        mentor_id: selectedMentor.id,
        session_date: sessionDate,
        session_time: sessionTime,
        duration_hours: duration,
        session_type: sessionType,
        notes: notes
      });
      
      setBookingData(res.data);
      setShowBooking(false);
      if (res.data.admin_bypass) {
        toast.success('Admin scheduled mentorship session without payment');
        loadMySessions();
        return;
      }
      setShowPayment(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  const handlePaymentSuccess = async () => {
    try {
      await api.post(`/mentors/sessions/${bookingData.session.id}/confirm`);
      toast.success('Session booked successfully!');
      setShowPayment(false);
      setBookingData(null);
      loadMySessions();
    } catch (error) {
      toast.error('Failed to confirm session');
    }
  };

  const formatPrice = (rate) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(rate);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="The Interview Logo" className="w-10 h-10" />
              <h1 className="text-2xl font-heading font-bold">Mentorship</h1>
            </div>
          </div>
          <div className="text-sm text-muted-foreground">{user?.full_name}</div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-heading font-bold mb-2">Connect with Expert Mentors</h2>
          <p className="text-muted-foreground">Get personalized guidance from industry professionals to accelerate your career</p>
        </div>

        {/* My Sessions */}
        {mySessions.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Your Upcoming Sessions</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mySessions.filter(s => s.status === 'scheduled').map((session) => (
                <Card key={session.id} className="border-l-4 border-l-blue-500" data-testid={`session-card-${session.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold">{session.mentor_name}</h4>
                      <Badge variant="secondary">{session.session_type}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="flex items-center gap-2">
                        <Calendar size={14} /> {session.session_date}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock size={14} /> {session.session_time} ({session.duration_hours}h)
                      </p>
                    </div>
                    {session.room_url && (
                      <Button 
                        size="sm" 
                        className="mt-3 w-full"
                        onClick={() => window.open(session.room_url, '_blank')}
                        data-testid={`join-session-${session.id}`}
                      >
                        <Video size={14} className="mr-2" /> Join Session
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Available Mentors */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-xl font-semibold">Available Mentors</h3>
          <span className="flex items-center gap-2 text-xs text-emerald-700"><ShieldCheck size={15} /> Mentor profiles are safety screened</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mentors.length === 0 ? (
            <p className="text-muted-foreground col-span-3 text-center py-12">No mentors available yet</p>
          ) : (
            mentors.filter(m => m.id).map((mentor) => (
              <Card key={mentor.id} className="hover:shadow-lg transition-shadow" data-testid={`mentor-card-${mentor.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{mentor.name}</CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <Star size={14} className="fill-yellow-400 text-yellow-400" />
                        {(mentor.rating || 5.0).toFixed(1)} • {mentor.total_sessions || 0} sessions
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{formatPrice(mentor.hourly_rate)}</div>
                      <div className="text-xs text-muted-foreground">per hour</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {mentor.expertise?.map((skill, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{mentor.bio}</p>
                  <Button 
                    className="w-full" 
                    onClick={() => {
                      setSelectedMentor(mentor);
                      setShowBooking(true);
                    }}
                    data-testid={`book-mentor-${mentor.id}`}
                  >
                    Book Session
                  </Button>
                  {mentor.user_id && mentor.user_id !== user?.id && (
                    <Button type="button" variant="ghost" size="sm" className="mt-2 w-full text-muted-foreground" onClick={() => setReportedMentor(mentor)}>
                      <Flag size={13} className="mr-2" /> Report mentor profile
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle>Book Session with {selectedMentor?.name}</DialogTitle>
            <DialogDescription>
              Rate: {selectedMentor && formatPrice(selectedMentor.hourly_rate)}/hour
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input 
                type="date" 
                value={sessionDate} 
                onChange={(e) => setSessionDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="session-date-input"
              />
            </div>
            
            <div>
              <Label>Time</Label>
              <Input 
                type="time" 
                value={sessionTime} 
                onChange={(e) => setSessionTime(e.target.value)}
                data-testid="session-time-input"
              />
            </div>
            
            <div>
              <Label>Duration (hours)</Label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger data-testid="duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="3">3 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Session Type</Label>
              <Select value={sessionType} onValueChange={setSessionType}>
                <SelectTrigger data-testid="session-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-on-1">
                    <div className="flex items-center gap-2">
                      <Users size={14} /> 1-on-1 Session
                    </div>
                  </SelectItem>
                  <SelectItem value="group">
                    <div className="flex items-center gap-2">
                      <Users size={14} /> Group Session
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What would you like to discuss?"
                data-testid="session-notes-input"
              />
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between text-sm mb-1">
                <span>Session Fee</span>
                <span>{selectedMentor && formatPrice(selectedMentor.hourly_rate * duration)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Platform fee (20%)</span>
                <span>{selectedMentor && formatPrice(selectedMentor.hourly_rate * duration * 0.2)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Mentor receives (80%)</span>
                <span>{selectedMentor && formatPrice(selectedMentor.hourly_rate * duration * 0.8)}</span>
              </div>
            </div>
            
            <Button className="w-full" onClick={handleBookSession} data-testid="proceed-to-payment-btn">
              <DollarSign size={16} className="mr-2" />
              {isAdmin ? 'Schedule Without Payment' : 'Proceed to Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Complete Payment</DialogTitle>
            <DialogDescription>
              Total: {bookingData && formatPrice(bookingData.total_amount)}
            </DialogDescription>
          </DialogHeader>
          
          {bookingData && (
            <StripeCheckout 
              clientSecret={bookingData.client_secret}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setShowPayment(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <SafetyReportDialog
        open={Boolean(reportedMentor)}
        onOpenChange={(open) => { if (!open) setReportedMentor(null); }}
        subject={reportedMentor ? { id: reportedMentor.user_id, name: reportedMentor.name } : null}
        sourceType="mentor_profile"
        sourceId={reportedMentor?.id || ''}
      />

      <Footer />
    </div>
  );
}
